import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";

import { getCurrentPlan, getAvailablePlans } from "../../../shared/services/adminApi";

import '../home/Dashboard.scss';

export default function CurrentPlan(props) {

    const [currentPlan, setCurrentPlan] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        setLoading(true);
        Promise.all([
            getCurrentPlan().catch(() => null),
            getAvailablePlans().catch(() => null)
        ]).then(([currentRes, availableRes]) => {
            if (currentRes?.data?.data) {
                setCurrentPlan(currentRes.data.data);
            }
            if (availableRes?.data) {
                setAvailablePlans(availableRes.data);
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            setError('Failed to load plan information');
        });
    };

    const formatCurrency = (amount, currency) => {
        if (!amount || amount === 0) return 'Free';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: currency || 'INR',
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const getBillingLabel = (cycle) => {
        const labels = {
            'FREE': 'Free',
            'PER_BUILDING': 'Per Building',
            'MONTHLY': '/month',
            'YEARLY': '/year',
            'CUSTOM': 'Custom',
        };
        return labels[cycle] || cycle;
    };

    if (loading) {
        return (
            <div className="main-content d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
                <img className='loader-icon' src={`${process.env.PUBLIC_URL}/static/images/loader.svg`} alt="loader-icon" />
            </div>
        );
    }

    return (
        <div className="main-content animate-fade-in">
            <div className="d-flex flex-column mt-3 mb-4">
                <h3 className="content-label">My Subscription</h3>
                <p className='content-sub-label fw-bold text-gray'>View your current plan and available subscriptions</p>
            </div>

            {error && (
                <div className="alert alert-danger">{error}</div>
            )}

            {/* Current Plan */}
            <h5 className="fw-bold mb-3">Current Plan</h5>
            {currentPlan && currentPlan.length > 0 ? (
                <Row className="g-3 mb-4">
                    {currentPlan.map((item, index) => (
                        <Col md={6} key={index}>
                            <Card className="shadow-sm h-100">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <h5 className="fw-bold">{item.plan?.name || 'N/A'}</h5>
                                            <span className="badge bg-success">{item.subscription?.status}</span>
                                        </div>
                                        <h4 className="text-primary fw-bold">
                                            {formatCurrency(item.plan?.price?.amount, item.plan?.price?.currency)}
                                            <small className="text-muted fs-6"> {getBillingLabel(item.plan?.price?.billing_cycle)}</small>
                                        </h4>
                                    </div>
                                    <hr />
                                    <p className="text-muted">{item.plan?.summary}</p>
                                    {item.plan?.features && item.plan.features.length > 0 && (
                                        <div className="mt-2">
                                            <small className="fw-bold">Features:</small>
                                            <ul className="mt-1">
                                                {item.plan.features.map((feature, fIndex) => (
                                                    <li key={fIndex} className="text-muted"><small>{feature}</small></li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {item.plan?.limits && (
                                        <div className="mt-3">
                                            <small className="fw-bold text-secondary">Plan Limits:</small>
                                            <div className="d-flex flex-column gap-1 mt-1 text-muted">
                                                <div>
                                                    <small>
                                                        <strong>Max Floors:</strong> {item.plan.limits.max_floor !== null && item.plan.limits.max_floor !== undefined ? `${item.plan.limits.max_floor + 1} (${item.plan.limits.max_floor === 0 ? "Ground Floor only" : `Up to Floor ${item.plan.limits.max_floor}`})` : "Unlimited"}
                                                    </small>
                                                </div>
                                                <div>
                                                    <small>
                                                        <strong>Allowed Building Types:</strong> {item.plan.limits.building_types && item.plan.limits.building_types.length > 0 ? item.plan.limits.building_types.join(", ") : "All Types"}
                                                    </small>
                                                </div>
                                                <div>
                                                    <small>
                                                        <strong>Allow New Project:</strong> {item.plan.code?.toUpperCase() === "FREE" ? "First project only" : "Yes"}
                                                    </small>
                                                </div>
                                                <div>
                                                    <small>
                                                        <strong>Max Estimations:</strong> {item.plan.limits.estimations !== null && item.plan.limits.estimations !== undefined ? item.plan.limits.estimations : "Unlimited"}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {item.subscription && (() => {
                                        const usagePercent = item.subscription.total_count 
                                            ? (item.subscription.used_count / item.subscription.total_count) * 100 
                                            : 0;
                                        return (
                                            <div className="mt-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <small className="text-muted">
                                                        Used: {item.subscription.used_count || 0} / {item.subscription.total_count || '∞'}
                                                        {item.subscription.available_count != null && ` | Available: ${item.subscription.available_count}`}
                                                    </small>
                                                    {item.subscription.available_count === 0 && (
                                                        <span className="badge bg-danger">Limit Reached</span>
                                                    )}
                                                    {item.subscription.available_count === 1 && (
                                                        <span className="badge bg-warning text-dark">Almost at limit</span>
                                                    )}
                                                </div>
                                                {item.subscription.total_count && (
                                                    <div className="usage-progress-bar-container">
                                                        <div 
                                                            className={`usage-progress-bar ${
                                                                usagePercent >= 90 ? "danger" : usagePercent >= 70 ? "warning" : "success"
                                                            }`}
                                                            style={{ width: `${Math.min(100, usagePercent)}%` }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <div className="alert alert-info">No active plan found. Please contact administrator.</div>
            )}

            {/* Available Plans */}
            <h5 className="fw-bold mb-3 mt-4">Available Plans</h5>
            {availablePlans && availablePlans.length > 0 ? (
                <Row className="g-3">
                    {availablePlans.map((plan, index) => (
                        <Col md={4} key={index}>
                            <Card className="shadow-sm h-100">
                                <Card.Body>
                                    <h5 className="fw-bold">{plan.plan_name || 'Plan'}</h5>
                                    <p className="text-muted">
                                        Available: {plan.available_count || 0}
                                        {plan.total_count > 0 && ` / ${plan.total_count}`}
                                    </p>
                                    <p className="text-muted">Used: {plan.used_count || 0}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <div className="alert alert-info">No available subscriptions found.</div>
            )}

            <div className="d-flex mt-4">
                <Button variant="secondary" onClick={() => props.history.goBack()}>Back</Button>
            </div>
        </div>
    );
}